import { ClickAwayListener } from "@mui/material";
import {
  CustomDate,
  DateFilterOperator,
  isRelativeTimeRange,
  KnownDateFilterTimeFrame,
  RelativeTime,
  RelativeTimeRange,
  RelativeTimeUnit,
  SingleRelativeTime,
} from "@redotech/redo-model/views/advanced-filters/date-filter";
import { assertNever, Tuple } from "@redotech/util/type";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Calendar from "react-calendar";
import { RedoCheckbox } from "../../arbiter-components/checkbox/redo-checkbox";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
  type RedoFilterGroupSize,
} from "../../arbiter-components/filter-group/redo-filter-group";
import {
  RedoTimePicker,
  TimeOfDay,
} from "../../arbiter-components/time/redo-time-picker";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import { Text } from "../../text";

// CSS imports must be ordered
import "react-time-picker/dist/TimePicker.css";
//
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
//
import "react-calendar/dist/Calendar.css";
//
import { useHandler } from "@redotech/react-util/hook";
import { AdvancedFilterType } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { RedoIncrementDecrement } from "../../arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoListItem } from "../../arbiter-components/list/redo-list";
import { RedoListDropdown } from "../../arbiter-components/list/redo-list-dropdown";
import "../../date-range.module.css";
import { SimpleFilterDropdown } from "../../filters/simple-filter-dropdown";
import { DateTableFilter } from "../advanced-filter";

export const DateFilterGroup = memo(function DateFilterGroup({
  filter,
  setFilter,
  removeFilter,
  openOnRender,
  size,
}: {
  filter: DateTableFilter;
  size?: RedoFilterGroupSize;
  setFilter(filter: DateTableFilter): void;
  removeFilter(): void;
  openOnRender: boolean;
}) {
  const { name, operator, value, customDate, type, relativeTime } = filter.data;

  const [valueRef, setValueRef] = useState<HTMLButtonElement | null>(null);

  // These two are only used for the relative time range dropdowns
  const [startValueRef, setStartValueRef] = useState<HTMLButtonElement | null>(
    null,
  );
  const [endValueRef, setEndValueRef] = useState<HTMLButtonElement | null>(
    null,
  );

  const handleSetCustomDateValue = useHandler((value: CustomDate) => {
    const sortedDates = sortDates(value);
    setFilter({
      ...filter,
      data: {
        type: AdvancedFilterType.DATE,
        name,
        operator,
        value: KnownDateFilterTimeFrame.CUSTOM,
        customDate: sortedDates,
        relativeTime: undefined,
      },
    });
  });

  const handleSetRelativeTimeValue = useHandler((value: RelativeTime) => {
    setFilter({
      ...filter,
      data: {
        type: AdvancedFilterType.DATE,
        name,
        operator,
        value: KnownDateFilterTimeFrame.RELATIVE,
        customDate: undefined,
        relativeTime: value,
      },
    });
  });

  const handleSetTimeFrame = useHandler(
    ({
      item: newTimeFrameItem,
    }: {
      item: RedoListItem<KnownDateFilterTimeFrame>;
    }) => {
      const newTimeFrame = newTimeFrameItem.value;
      if (newTimeFrame === KnownDateFilterTimeFrame.CUSTOM) {
        setCustomDateDropdownOpen(true);
        return;
      }
      if (newTimeFrame === KnownDateFilterTimeFrame.RELATIVE) {
        setFilter({
          ...filter,
          data: {
            type,
            operator,
            value: newTimeFrame,
            customDate: undefined,
            relativeTime:
              operator === DateFilterOperator.WITHIN
                ? {
                    start: { value: 1, unit: RelativeTimeUnit.HOURS },
                    end: { value: 12, unit: RelativeTimeUnit.HOURS },
                  }
                : { value: 12, unit: RelativeTimeUnit.HOURS },
            name,
          },
        });
        return;
      }
      const oldTimeFrame = value;
      if (newTimeFrame === oldTimeFrame) {
        return;
      }
      setFilter({
        ...filter,
        data: {
          type,
          operator,
          value: newTimeFrame,
          customDate: undefined,
          relativeTime: undefined,
          name,
        },
      });
    },
  );

  const handleSetDateOperatorType = useHandler(
    (newOperatorType: DateFilterOperator) => {
      const oldOpType = operator;
      if (newOperatorType === oldOpType) {
        return;
      }
      const newCustomDate =
        value === KnownDateFilterTimeFrame.CUSTOM
          ? wrangleCustomDateIntoProperForm(newOperatorType, customDate)
          : undefined;

      // Just resetting relative time for now when the operator changes, could be improved to coerce to fit into the new operator
      const newRelativeTime = undefined;
      const newValue =
        value === KnownDateFilterTimeFrame.RELATIVE ? null : value;

      setFilter({
        ...filter,
        data: {
          type: AdvancedFilterType.DATE,
          operator: newOperatorType,
          value: newValue,
          name,
          customDate: newCustomDate,
          relativeTime: newRelativeTime,
        },
      });
    },
  );

  const [customDateDropdownOpen, setCustomDateDropdownOpen] = useState(false);
  const [relativeStartDropdownOpen, setRelativeStartDropdownOpen] =
    useState(false);
  const [relativeEndDropdownOpen, setRelativeEndDropdownOpen] = useState(false);

  const [valueDropdownOpen, setValueDropdownOpen] = useState(false);

  const isRelativeWithinOperator =
    operator === DateFilterOperator.WITHIN &&
    value === KnownDateFilterTimeFrame.RELATIVE &&
    relativeTime &&
    isRelativeTimeRange(relativeTime);

  const getRelativeTimeText = (time: SingleRelativeTime): string => {
    const unit = getUnitDisplayText(time.value, time.unit);
    return `${time.value} ${unit} ago`;
  };

  const handleValueAnchorClick = useHandler(() => {
    if (
      value === KnownDateFilterTimeFrame.RELATIVE &&
      operator !== DateFilterOperator.WITHIN
    ) {
      setRelativeStartDropdownOpen(!relativeStartDropdownOpen);
    } else if (value === KnownDateFilterTimeFrame.CUSTOM) {
      setCustomDateDropdownOpen(!customDateDropdownOpen);
    } else {
      setValueDropdownOpen(!valueDropdownOpen);
    }
  });

  const valueAnchor = !isRelativeWithinOperator ? (
    <RedoFilterDropdownAnchor
      color="primary"
      onClick={handleValueAnchorClick}
      ref={setValueRef}
      text={getValueString(value, customDate, relativeTime)}
      weight="medium"
    />
  ) : null;

  const relativeStartAnchor =
    isRelativeWithinOperator &&
    relativeTime &&
    isRelativeTimeRange(relativeTime) ? (
      <RedoFilterDropdownAnchor
        color="primary"
        onClick={() => setRelativeStartDropdownOpen(!relativeStartDropdownOpen)}
        ref={setStartValueRef}
        text={getRelativeTimeText(relativeTime.end)}
        weight="medium"
      />
    ) : null;

  const relativeEndAnchor =
    isRelativeWithinOperator &&
    relativeTime &&
    isRelativeTimeRange(relativeTime) ? (
      <RedoFilterDropdownAnchor
        color="primary"
        onClick={() => setRelativeEndDropdownOpen(!relativeEndDropdownOpen)}
        ref={setEndValueRef}
        text={getRelativeTimeText(relativeTime.start)}
        weight="medium"
      />
    ) : null;

  const valueOptions = useMemo(() => {
    return Object.values(KnownDateFilterTimeFrame).map<
      RedoListItem<KnownDateFilterTimeFrame>
    >((item) => {
      return {
        id: item,
        type: "text",
        text: timeFrameToText[item],
        value: item,
      };
    });
  }, []);

  const valueDropdown = (
    <RedoListDropdown
      dropdownAnchor={valueRef}
      dropdownOpen={valueDropdownOpen}
      items={valueOptions}
      itemSelected={handleSetTimeFrame}
      selectedItems={value || undefined}
      setDropdownOpen={setValueDropdownOpen}
      size="xs"
    />
  );

  return (
    <>
      {valueDropdown}
      <CustomDatePickerDropdown
        anchor={valueRef}
        customDate={customDate}
        open={customDateDropdownOpen}
        operator={operator}
        setCustomDate={handleSetCustomDateValue}
        setOpen={setCustomDateDropdownOpen}
      />
      {!isRelativeWithinOperator &&
        value === KnownDateFilterTimeFrame.RELATIVE && (
          <RelativeTimePickerDropdown
            anchor={valueRef}
            open={relativeStartDropdownOpen}
            operator={operator}
            relativeTime={relativeTime}
            setOpen={setRelativeStartDropdownOpen}
            setRelativeTime={handleSetRelativeTimeValue}
          />
        )}
      {isRelativeWithinOperator && (
        <>
          <RelativeTimePickerDropdown
            anchor={startValueRef}
            field="start"
            open={relativeStartDropdownOpen}
            operator={operator}
            relativeTime={relativeTime}
            setOpen={setRelativeStartDropdownOpen}
            setRelativeTime={handleSetRelativeTimeValue}
          />
          <RelativeTimePickerDropdown
            anchor={endValueRef}
            field="end"
            open={relativeEndDropdownOpen}
            operator={operator}
            relativeTime={relativeTime}
            setOpen={setRelativeEndDropdownOpen}
            setRelativeTime={handleSetRelativeTimeValue}
          />
        </>
      )}
      <RedoFilterGroup
        Icon={filter.Icon}
        propertyName={filter.displayName}
        query={
          <SimpleFilterDropdown
            filterStyle="query"
            options={filter.operators || Object.values(DateFilterOperator)}
            optionToFriendlyName={(value) =>
              dateFilterOperatorTypeToText[value]
            }
            setValue={handleSetDateOperatorType}
            value={operator}
          />
        }
        removeFilter={removeFilter}
        size={size}
        value={
          isRelativeWithinOperator ? (
            <>
              {relativeStartAnchor}
              <Flex align="center">
                <Text fontSize="sm" px="sm" textColor="secondary">
                  and
                </Text>
              </Flex>
              {relativeEndAnchor}
            </>
          ) : (
            valueAnchor
          )
        }
      />
    </>
  );
});

type TimeOfDayTuple = Tuple<TimeOfDay, 1> | Tuple<TimeOfDay, 2>;

export function CustomDatePickerDropdown({
  anchor,
  open,
  setOpen,
  customDate,
  setCustomDate,
  operator,
  allowCustomizeTimeOfDay = true,
  allowFutureDates = false,
}: {
  anchor: HTMLElement | null;
  open: boolean;
  setOpen(open: boolean): void;
  customDate: CustomDate | null | undefined;
  setCustomDate(dates: CustomDate): void;
  operator: DateFilterOperator;
  allowCustomizeTimeOfDay?: boolean;
  allowFutureDates?: boolean;
}) {
  const [timesOfDayFromPicker, setTimesOfDayFromPicker] = useState<
    TimeOfDayTuple | undefined | null
  >();

  const [localCustomDates, setLocalCustomDates] = useState<
    CustomDate | null | undefined
  >(customDate);

  useEffect(() => {
    setLocalCustomDates(customDate);
    setTimesOfDayFromPicker(undefined);
  }, [customDate]);

  const [customizeTimeOfDay, setCustomizeTimeOfDay] = useState(false);

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const timesOfDay: TimeOfDayTuple | undefined = useMemo(() => {
    if (!customizeTimeOfDay || !localCustomDates) {
      return undefined;
    }

    if (localCustomDates?.length === 2) {
      const timesOfDayFromDates = [
        localCustomDates[0],
        localCustomDates[1],
      ].map((date) => {
        return { hours: date.getHours(), minutes: date.getMinutes() };
      }) as Tuple<TimeOfDay, 2>;

      if (timesOfDayFromPicker) {
        if (timesOfDayFromPicker.length === 2) {
          return timesOfDayFromPicker;
        } else {
          return [timesOfDayFromPicker[0], timesOfDayFromDates[1]];
        }
      } else {
        return timesOfDayFromDates;
      }
    } else {
      if (timesOfDayFromPicker) {
        return timesOfDayFromPicker;
      }
      return [
        {
          hours: localCustomDates[0].getHours(),
          minutes: localCustomDates[0].getMinutes(),
        },
      ];
    }
  }, [localCustomDates, timesOfDayFromPicker, customizeTimeOfDay]);

  function handleClose(datesToCloseWith?: CustomDate) {
    const dates = datesToCloseWith || localCustomDates;

    if (!dates) {
      setOpen(false);
      return;
    }

    if (!timesOfDay) {
      setCustomDate(dates);
      setOpen(false);
      return;
    }

    if (timesOfDay?.length === 2 && dates?.length === 2) {
      const [startTime, endTime] = timesOfDay;
      const [startDate, endDate] = dates;
      const newCustomDate: [Date, Date] = [
        new Date(startDate),
        new Date(endDate),
      ];
      newCustomDate[0].setHours(startTime.hours, startTime.minutes);
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      newCustomDate[1].setHours(endTime.hours, endTime.minutes);
      setCustomDate(newCustomDate);
    } else if (timesOfDay?.length === 1 && dates?.length === 1) {
      const newCustomDate = [new Date(dates[0])];
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      newCustomDate[0].setHours(timesOfDay[0].hours, timesOfDay[0].minutes);
      setCustomDate(newCustomDate as CustomDate);
    }

    setOpen(false);
  }

  function handleSetCustomizeTimeOfDay(customize: boolean) {
    if (!customize) {
      setTimesOfDayFromPicker(undefined);
      setLocalCustomDates(
        wrangleDatePickerSubmission(operator, localCustomDates || null),
      );
    }
    setCustomizeTimeOfDay(customize);
  }

  function handleSetCustomDate(value: CustomDate) {
    const formattedCustomDate = wrangleDatePickerSubmission(
      operator,
      Array.isArray(value) ? value : [value],
    );
    setLocalCustomDates(formattedCustomDate);
    if (!customizeTimeOfDay) {
      handleClose(formattedCustomDate);
    }
  }

  return (
    <>
      {open && (
        <ClickAwayListener onClickAway={() => handleClose()}>
          <Dropdown
            anchor={anchor}
            constrainHeight={false}
            fitToAnchor={false}
            open={open}
          >
            <Flex dir="column">
              <Calendar
                maxDate={allowFutureDates ? undefined : new Date()}
                minDetail="year"
                onChange={handleSetCustomDate as any}
                selectRange={operator === DateFilterOperator.WITHIN}
                value={localCustomDates as any}
              />
              {allowCustomizeTimeOfDay && (
                <RedoCheckbox
                  label="Customize time of day"
                  setValue={(value) => handleSetCustomizeTimeOfDay(!!value)}
                  value={customizeTimeOfDay}
                />
              )}
              {customizeTimeOfDay &&
                localCustomDates?.length === 2 &&
                timesOfDay?.length === 2 && (
                  <RangeTimePicker
                    dates={localCustomDates}
                    setTimesOfDay={setTimesOfDayFromPicker}
                    timesOfDay={timesOfDay}
                  />
                )}
              {customizeTimeOfDay &&
                localCustomDates &&
                timesOfDay &&
                localCustomDates.length === 1 &&
                timesOfDay.length === 1 && (
                  <SingleDateTimePicker
                    date={localCustomDates[0]}
                    setTimeOfDay={setTimesOfDayFromPicker}
                    timeOfDay={timesOfDay[0]}
                  />
                )}
            </Flex>
          </Dropdown>
        </ClickAwayListener>
      )}
    </>
  );
}

export function RelativeTimePickerDropdown({
  anchor,
  open,
  setOpen,
  relativeTime,
  setRelativeTime,
  operator,
  field,
}: {
  anchor: HTMLElement | null;
  open: boolean;
  setOpen(open: boolean): void;
  relativeTime: RelativeTime | null | undefined;
  setRelativeTime(time: RelativeTime): void;
  operator: DateFilterOperator;
  field?: "start" | "end";
}) {
  const isWithinOperator = operator === DateFilterOperator.WITHIN;

  const getDefaultRelativeTime = useCallback((): RelativeTime => {
    if (isWithinOperator) {
      return {
        start: { value: 1, unit: RelativeTimeUnit.HOURS },
        end: { value: 12, unit: RelativeTimeUnit.HOURS },
      };
    }
    return { value: 12, unit: RelativeTimeUnit.HOURS };
  }, [isWithinOperator]);

  const [localRelativeTime, setLocalRelativeTime] = useState<RelativeTime>(
    relativeTime || getDefaultRelativeTime(),
  );

  useEffect(() => {
    setLocalRelativeTime(relativeTime || getDefaultRelativeTime());
  }, [relativeTime, isWithinOperator, getDefaultRelativeTime]);

  const convertToMilliseconds = (time: SingleRelativeTime): number => {
    switch (time.unit) {
      case RelativeTimeUnit.MINUTES:
        return time.value * 60 * 1000;
      case RelativeTimeUnit.HOURS:
        return time.value * 60 * 60 * 1000;
      case RelativeTimeUnit.DAYS:
        return time.value * 24 * 60 * 60 * 1000;
      default:
        assertNever(time.unit);
    }
  };

  // If the end time that they select is before the start time, swap them
  const handleClose = useHandler(() => {
    setOpen(false);

    if (!localRelativeTime) {
      return;
    }

    if (!isWithinOperator || !isRelativeTimeRange(localRelativeTime)) {
      setRelativeTime(localRelativeTime);
      return;
    }

    const startMillis = convertToMilliseconds(localRelativeTime.start);
    const endMillis = convertToMilliseconds(localRelativeTime.end);

    if (startMillis > endMillis) {
      setRelativeTime({
        start: localRelativeTime.end,
        end: localRelativeTime.start,
      });
    } else {
      setRelativeTime(localRelativeTime);
    }
  });

  const handleValueChange = useHandler(
    (value: number, field?: keyof RelativeTimeRange) => {
      if (isWithinOperator && isRelativeTimeRange(localRelativeTime) && field) {
        setLocalRelativeTime({
          ...localRelativeTime,
          [field]: { ...localRelativeTime[field], value },
        });
      } else if (!isWithinOperator) {
        setLocalRelativeTime({ ...localRelativeTime, value });
      }
    },
  );

  const handleUnitChange = useHandler(
    (unit: RelativeTimeUnit, field?: keyof RelativeTimeRange) => {
      if (isWithinOperator && isRelativeTimeRange(localRelativeTime) && field) {
        setLocalRelativeTime({
          ...localRelativeTime,
          [field]: { ...localRelativeTime[field], unit },
        });
      } else if (!isWithinOperator) {
        setLocalRelativeTime({ ...localRelativeTime, unit });
      }
    },
  );

  const showFieldSpecificDropdown =
    field && isWithinOperator && isRelativeTimeRange(localRelativeTime);

  return (
    <>
      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <Dropdown
            anchor={anchor}
            constrainHeight={false}
            fitToAnchor={false}
            flexProps={{ radius: "xs", p: "sm" }}
            open={open}
          >
            <Flex dir="column" gap="xs">
              {showFieldSpecificDropdown ? (
                <Flex align="center" gap="xs">
                  <RedoIncrementDecrement
                    max={1000}
                    min={1}
                    setValue={(value) =>
                      handleValueChange(
                        value,
                        field === "start" ? "end" : "start",
                      )
                    }
                    size="small"
                    value={
                      field === "start"
                        ? localRelativeTime.end.value
                        : localRelativeTime.start.value
                    }
                  />
                  <SimpleFilterDropdown
                    filterStyle="value"
                    options={Object.values(RelativeTimeUnit)}
                    optionToFriendlyName={(unit) => unit}
                    setValue={(unit) =>
                      handleUnitChange(
                        unit,
                        field === "start" ? "end" : "start",
                      )
                    }
                    value={
                      field === "start"
                        ? localRelativeTime.end.unit
                        : localRelativeTime.start.unit
                    }
                  />
                </Flex>
              ) : !isRelativeTimeRange(localRelativeTime) ? (
                <Flex align="center" gap="sm">
                  <RedoIncrementDecrement
                    max={1000}
                    min={1}
                    setValue={(value) => handleValueChange(value)}
                    size="small"
                    value={localRelativeTime.value}
                  />
                  <SimpleFilterDropdown
                    filterStyle="value"
                    options={Object.values(RelativeTimeUnit)}
                    optionToFriendlyName={(unit) => unit}
                    setValue={(unit) => handleUnitChange(unit)}
                    value={localRelativeTime.unit}
                  />
                </Flex>
              ) : null}
            </Flex>
          </Dropdown>
        </ClickAwayListener>
      )}
    </>
  );
}

function dateToTimeOfDay(date: Date): TimeOfDay {
  return { hours: date.getHours(), minutes: date.getMinutes() };
}

function SingleDateTimePicker({
  date,
  timeOfDay,
  setTimeOfDay,
}: {
  date: Date;
  timeOfDay: TimeOfDay;
  setTimeOfDay(date: TimeOfDayTuple): void;
}) {
  function handleSetStartTimeVal(newTimeOfDay: TimeOfDay | null) {
    const timeOfDayToSet = newTimeOfDay || dateToTimeOfDay(date);
    setTimeOfDay([timeOfDayToSet]);
  }

  return (
    <Flex justify="space-between">
      <Text>{date.toDateString()}</Text>
      <Text> at </Text>
      <RedoTimePicker setValue={handleSetStartTimeVal} value={timeOfDay} />
    </Flex>
  );
}

function RangeTimePicker({
  dates,
  timesOfDay,
  setTimesOfDay,
}: {
  dates: Tuple<Date, 2>;
  timesOfDay: Tuple<TimeOfDay, 2>;
  setTimesOfDay(date: Tuple<TimeOfDay, 2>): void;
}) {
  function handleSetStartTimeVal(newTimeOfDay: TimeOfDay | null) {
    const timeOfDayToSet = newTimeOfDay || dateToTimeOfDay(dates[0]);
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    setTimesOfDay([timeOfDayToSet, timesOfDay[1]]);
  }

  function handleSetEndTimeVal(newTimeOfDay: TimeOfDay | null) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const timeOfDayToSet = newTimeOfDay || dateToTimeOfDay(dates[1]);
    setTimesOfDay([timesOfDay[0], timeOfDayToSet]);
  }

  return (
    <Flex dir="column">
      <Flex justify="space-between">
        <Text>{dates[0].toDateString()}</Text>
        <Text> at </Text>
        <RedoTimePicker
          setValue={handleSetStartTimeVal}
          value={timesOfDay[0]}
        />
      </Flex>
      <Flex justify="space-between">
        {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
        <Text>{dates[1].toDateString()}</Text>
        <Text> at </Text>
        {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
        <RedoTimePicker setValue={handleSetEndTimeVal} value={timesOfDay[1]} />
      </Flex>
    </Flex>
  );
}

function formatDateTime(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleString("en-US", options).replace(", ", " at ");
}

function getUnitDisplayText(value: number, unit: RelativeTimeUnit): string {
  if (value === 1) {
    switch (unit) {
      case RelativeTimeUnit.MINUTES:
        return "minute";
      case RelativeTimeUnit.HOURS:
        return "hour";
      case RelativeTimeUnit.DAYS:
        return "day";
      default:
        assertNever(unit);
    }
  }
  return unit;
}

function getValueString(
  timeFrame: KnownDateFilterTimeFrame | null | undefined,
  customDate: CustomDate | null | undefined,
  relativeTime: RelativeTime | null | undefined,
): string {
  if (!timeFrame) {
    return "...";
  }
  if (
    timeFrame === KnownDateFilterTimeFrame.RELATIVE &&
    relativeTime &&
    !isRelativeTimeRange(relativeTime) // Guaranteed to not be true by caller
  ) {
    const unit = getUnitDisplayText(relativeTime.value, relativeTime.unit);
    return `${relativeTime.value} ${unit} ago`;
  }
  if (!customDate || timeFrame !== KnownDateFilterTimeFrame.CUSTOM) {
    return timeFrameToText[timeFrame];
  }
  if (customDate?.length === 2) {
    const [startDate, endDate] = customDate;
    return `${formatDateTime(startDate)} - ${formatDateTime(endDate)}`;
  }
  return formatDateTime(customDate[0]);
}

export const timeFrameToText: Record<KnownDateFilterTimeFrame, string> = {
  [KnownDateFilterTimeFrame.TODAY]: "Today",
  [KnownDateFilterTimeFrame.THIS_WEEK]: "This Week",
  [KnownDateFilterTimeFrame.LAST_WEEK]: "Last Week",
  [KnownDateFilterTimeFrame.THIS_MONTH]: "This Month",
  [KnownDateFilterTimeFrame.LAST_MONTH]: "Last Month",
  [KnownDateFilterTimeFrame.THIS_YEAR]: "This Year",
  [KnownDateFilterTimeFrame.LAST_YEAR]: "Last Year",
  [KnownDateFilterTimeFrame.CUSTOM]: "Custom",
  [KnownDateFilterTimeFrame.RELATIVE]: "Relative",
};

export const dateFilterOperatorTypeToText: Record<DateFilterOperator, string> =
  {
    [DateFilterOperator.WITHIN]: "during",
    [DateFilterOperator.BEFORE]: "before",
    [DateFilterOperator.AFTER]: "after",
  };

export function sortDates(dates: CustomDate): CustomDate {
  return dates.sort((a, b) => a.getTime() - b.getTime()) as CustomDate;
}

export function wrangleDatePickerSubmission(
  operator: DateFilterOperator,
  value: CustomDate | null | undefined,
): CustomDate {
  if (value?.length === 2) {
    const [startDate, endDate] = value;
    const definedStartDate = startDate || new Date();
    const definedEndDate = endDate || new Date();
    const newCustomDateRange = sortDates([definedStartDate, definedEndDate]);

    const newCustomDate = wrangleCustomDateIntoProperForm(
      operator,
      newCustomDateRange,
    );

    return newCustomDate;
  } else if (value) {
    const newCustomDate = wrangleCustomDateIntoProperForm(operator, value);
    return newCustomDate;
  } else {
    const newCustomDate = wrangleCustomDateIntoProperForm(operator, [
      new Date(),
    ]);
    return newCustomDate;
  }
}

export function wrangleCustomDateIntoProperForm(
  operatorType: DateFilterOperator,
  customDate: CustomDate | undefined | null,
): CustomDate {
  switch (operatorType) {
    case DateFilterOperator.WITHIN: {
      if (customDate?.length === 2) {
        return customDate;
      } else if (customDate?.length === 1) {
        const startOfDay = new Date(customDate[0]);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(customDate[0]);
        endOfDay.setHours(23, 59, 59, 999);
        return [startOfDay, endOfDay];
      } else {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);
        return [startOfDay, endOfDay];
      }
    }
    case DateFilterOperator.BEFORE: {
      if (customDate && customDate.length > 0) {
        const beforeDate = new Date(customDate[0]);
        beforeDate.setHours(0, 0, 0, 0);
        return [beforeDate];
      } else {
        const beforeDate = new Date();
        beforeDate.setHours(0, 0, 0, 0);
        return [beforeDate];
      }
    }
    case DateFilterOperator.AFTER: {
      if (customDate?.length === 2) {
        const afterDate = new Date(customDate[1]);
        afterDate.setHours(23, 59, 59, 999);
        return [afterDate];
      } else if (customDate?.length === 1) {
        const afterDate = new Date(customDate[0]);
        afterDate.setHours(23, 59, 59, 999);
        return [afterDate];
      } else {
        const afterDate = new Date();
        afterDate.setHours(23, 59, 59, 999);
        return [afterDate];
      }
    }
    default: {
      assertNever(operatorType);
    }
  }
}
